import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Plus, Pencil, Trash2, Shield, ShieldOff, Search, Package } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(false);

  // Products state
  const [products, setProducts] = useState([]);
  const [productFilterGroup, setProductFilterGroup] = useState('all');
  const [productSearch, setProductSearch] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [editProductName, setEditProductName] = useState('');
  const [editProductGroup, setEditProductGroup] = useState('');
  const [productLoading, setProductLoading] = useState(false);

  // User edit state
  const [editingUser, setEditingUser] = useState(null);
  const [editUserName, setEditUserName] = useState('');
  const [editUserPassword, setEditUserPassword] = useState('');

  useEffect(() => { fetchGroups(); fetchUsers(); }, []);

  const fetchGroups = async () => {
    try { const { data } = await api.get('/product-groups'); setGroups(data.sort((a, b) => (a.sort_order || 999) - (b.sort_order || 999))); } catch (err) { console.error('Fetch groups error:', err); }
  };
  const fetchUsers = async () => {
    try { const { data } = await api.get('/admin/users'); setUsers(data); } catch (err) { console.error('Fetch users error:', err); }
  };

  const fetchProducts = async (groupId, search) => {
    try {
      const params = new URLSearchParams();
      if (groupId && groupId !== 'all') params.append('group_id', groupId);
      if (search) params.append('q', search);
      const { data } = await api.get(`/admin/products?${params.toString()}`);
      setProducts(data);
    } catch { setProducts([]); }
  };

  useEffect(() => { fetchProducts(productFilterGroup, productSearch); }, [productFilterGroup, productSearch]);

  const handleSaveGroup = async () => {
    if (!groupName.trim()) return;
    setLoading(true);
    try {
      if (editingGroup) {
        const { data } = await api.put(`/product-groups/${editingGroup.id}`, { name: groupName });
        setGroups(prev => prev.map(g => g.id === editingGroup.id ? { ...g, ...data } : g));
        toast.success('Varegruppe opdateret');
      } else {
        const { data } = await api.post('/product-groups', { name: groupName });
        setGroups(prev => [...prev, data]);
        toast.success('Varegruppe oprettet');
      }
      setShowGroupDialog(false); setEditingGroup(null); setGroupName('');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm('Er du sikker på at du vil slette denne varegruppe?')) return;
    try {
      await api.delete(`/product-groups/${id}`);
      setGroups(prev => prev.filter(g => g.id !== id));
      toast.success('Varegruppe slettet');
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleToggleAdmin = async (userId, currentRole) => {
    const action = currentRole === 'admin' ? 'remove-admin' : 'make-admin';
    if (!window.confirm(currentRole === 'admin' ? 'Fjern admin status?' : 'Gør denne bruger til admin?')) return;
    try {
      await api.post(`/admin/${action}`, { user_id: userId });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: currentRole === 'admin' ? 'user' : 'admin' } : u));
      toast.success(currentRole === 'admin' ? 'Admin status fjernet' : 'Bruger er nu admin');
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setEditProductName(product.name);
    setEditProductGroup(product.group_id);
  };

  const handleSaveProduct = async () => {
    if (!editingProduct || !editProductName.trim()) return;
    setProductLoading(true);
    try {
      const { data } = await api.put(`/admin/products/${editingProduct.id}`, {
        name: editProductName.trim(), group_id: editProductGroup
      });
      setProducts(prev => prev.map(p => p.id === editingProduct.id ? data : p));
      setEditingProduct(null);
      toast.success('Vare opdateret');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setProductLoading(false); }
  };

  const handleDeleteProduct = async () => {
    if (!editingProduct || !window.confirm(`Er du sikker på at du vil slette "${editingProduct.name}" permanent?`)) return;
    setProductLoading(true);
    try {
      await api.delete(`/admin/products/${editingProduct.id}`);
      setProducts(prev => prev.filter(p => p.id !== editingProduct.id));
      setEditingProduct(null);
      toast.success('Vare slettet permanent');
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setProductLoading(false); }
  };

  const handleSaveUserName = async () => {
    if (!editingUser || !editUserName.trim()) return;
    try {
      const payload = { name: editUserName.trim() };
      if (editUserPassword.trim()) payload.password = editUserPassword.trim();
      await api.put(`/admin/users/${editingUser.id}`, payload);
      setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, name: editUserName.trim() } : u));
      setEditingUser(null);
      setEditUserPassword('');
      toast.success(editUserPassword.trim() ? 'Bruger opdateret med nyt password' : 'Brugernavn opdateret');
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="admin-page">
      <div className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-[#EAE6DF] shadow-sm">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-[#5C706A] hover:text-[#2F3E3B]"
            data-testid="back-to-dashboard"><ArrowLeft className="w-4 h-4 mr-1" />Tilbage</Button>
          <h1 className="text-xl text-[#2F3E3B] font-medium">Administration</h1>
        </div>
      </div>
      <main className="max-w-4xl mx-auto px-4 py-6">
        <Tabs defaultValue="groups">
          <TabsList className="bg-[#EAE6DF]/50 mb-6">
            <TabsTrigger value="groups" className="font-sans data-[state=active]:bg-white" data-testid="tab-groups">Varegrupper</TabsTrigger>
            <TabsTrigger value="products" className="font-sans data-[state=active]:bg-white" data-testid="tab-products">Varer</TabsTrigger>
            <TabsTrigger value="users" className="font-sans data-[state=active]:bg-white" data-testid="tab-users">Brugere</TabsTrigger>
          </TabsList>

          {/* Varegrupper tab */}
          <TabsContent value="groups">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl text-[#2F3E3B]">Varegrupper</h2>
              <Button onClick={() => { setEditingGroup(null); setGroupName(''); setShowGroupDialog(true); }}
                className="bg-[#7F9C82] hover:bg-[#658368] text-white font-sans" data-testid="add-group-button">
                <Plus className="w-4 h-4 mr-1" />Ny varegruppe</Button>
            </div>
            {groups.length === 0 ? (
              <Card className="border-[#EAE6DF] p-8 text-center">
                <p className="text-[#5C706A] font-sans">Ingen varegrupper oprettet endnu.</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {groups.map(group => (
                  <Card key={group.id} className="p-4 border-[#EAE6DF] flex items-center justify-between" data-testid={`group-${group.id}`}>
                    <span className="font-sans font-medium text-[#2F3E3B]">{group.name}</span>
                    <div className="flex gap-1">
                      <button onClick={() => { setEditingGroup(group); setGroupName(group.name); setShowGroupDialog(true); }}
                        className="p-2 rounded-md hover:bg-[#EAE6DF] text-[#5C706A]" data-testid={`edit-group-${group.id}`}>
                        <Pencil className="w-4 h-4" /></button>
                      <button onClick={() => handleDeleteGroup(group.id)}
                        className="p-2 rounded-md hover:bg-red-50 text-[#C85547]" data-testid={`delete-group-${group.id}`}>
                        <Trash2 className="w-4 h-4" /></button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Varer tab */}
          <TabsContent value="products">
            <h2 className="text-2xl text-[#2F3E3B] mb-4">Varer</h2>
            <div className="flex gap-3 mb-4">
              <Select value={productFilterGroup} onValueChange={setProductFilterGroup}>
                <SelectTrigger className="w-56 border-[#EAE6DF] font-sans" data-testid="product-filter-group">
                  <SelectValue placeholder="Alle varegrupper" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#EAE6DF]">
                  <SelectItem value="all" className="font-sans">Alle varegrupper</SelectItem>
                  {groups.map(g => <SelectItem key={g.id} value={g.id} className="font-sans">{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C706A]" />
                <Input value={productSearch} onChange={e => setProductSearch(e.target.value)}
                  placeholder="Søg vare..." className="pl-10 border-[#EAE6DF] font-sans" data-testid="product-search-input" />
              </div>
            </div>
            {products.length === 0 ? (
              <Card className="border-[#EAE6DF] p-8 text-center">
                <Package className="w-10 h-10 mx-auto mb-3 text-[#EAE6DF]" />
                <p className="text-[#5C706A] font-sans">{productSearch ? 'Ingen varer fundet' : 'Vælg en varegruppe eller søg'}</p>
              </Card>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-[#5C706A] font-sans mb-2">{products.length} varer</p>
                {products.map(product => (
                  <Card key={product.id} className="p-3 border-[#EAE6DF] flex items-center justify-between hover:border-[#7F9C82]/40 transition-colors"
                    data-testid={`product-${product.id}`}>
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className="font-sans font-medium text-[#2F3E3B] truncate">{product.name}</span>
                      <Badge variant="outline" className="text-xs border-[#EAE6DF] text-[#5C706A] shrink-0">{product.group_name}</Badge>
                    </div>
                    <button onClick={() => handleEditProduct(product)}
                      className="p-2 rounded-md hover:bg-[#EAE6DF] text-[#5C706A] shrink-0" data-testid={`edit-product-${product.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Brugere tab */}
          <TabsContent value="users">
            <h2 className="text-2xl text-[#2F3E3B] mb-4">Brugere</h2>
            <div className="space-y-2">
              {users.map(u => (
                <Card key={u.id} className="p-4 border-[#EAE6DF] flex items-center justify-between" data-testid={`user-${u.id}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-sans font-medium text-[#2F3E3B]">{u.name || u.email}</span>
                      <Badge variant={u.role === 'admin' ? 'default' : 'outline'}
                        className={u.role === 'admin' ? 'bg-[#7F9C82] text-white text-xs' : 'text-xs border-[#EAE6DF] text-[#5C706A]'}>
                        {u.role === 'admin' ? 'Admin' : 'Bruger'}
                      </Badge>
                    </div>
                    <p className="text-sm text-[#5C706A] font-sans">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setEditingUser(u); setEditUserName(u.name || ''); setEditUserPassword(''); }}
                      className="p-2 rounded-md hover:bg-[#EAE6DF] text-[#5C706A]" data-testid={`edit-user-${u.id}`}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    {u.id !== user?._id && u.id !== user?.id && (
                      <Button variant="outline" size="sm" onClick={() => handleToggleAdmin(u.id, u.role)}
                        className={u.role === 'admin' ? 'border-[#C85547] text-[#C85547] hover:bg-red-50' : 'border-[#7F9C82] text-[#7F9C82] hover:bg-[#7F9C82]/5'}
                        data-testid={`toggle-admin-${u.id}`}>
                        {u.role === 'admin' ? <><ShieldOff className="w-3.5 h-3.5 mr-1" />Fjern admin</> : <><Shield className="w-3.5 h-3.5 mr-1" />Gør admin</>}
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Group dialog */}
      <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
        <DialogContent className="bg-white border-[#EAE6DF]">
          <DialogHeader>
            <DialogTitle className="text-[#2F3E3B]">{editingGroup ? 'Rediger varegruppe' : 'Ny varegruppe'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="font-sans text-[#2F3E3B]">Navn</Label>
            <Input value={groupName} onChange={e => setGroupName(e.target.value)} placeholder="F.eks. Mejeri, Frugt..."
              className="border-[#EAE6DF]" data-testid="group-name-input" onKeyDown={e => e.key === 'Enter' && handleSaveGroup()} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGroupDialog(false)} className="font-sans">Annuller</Button>
            <Button onClick={handleSaveGroup} disabled={loading || !groupName.trim()}
              className="bg-[#7F9C82] hover:bg-[#658368] text-white font-sans" data-testid="save-group-button">
              {loading ? 'Gemmer...' : 'Gem'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product edit dialog */}
      <Dialog open={!!editingProduct} onOpenChange={() => setEditingProduct(null)}>
        <DialogContent className="bg-white border-[#EAE6DF]">
          <DialogHeader>
            <DialogTitle className="text-[#2F3E3B]">Rediger vare</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="font-sans text-[#2F3E3B]">Varenavn</Label>
              <Input value={editProductName} onChange={e => setEditProductName(e.target.value)}
                className="border-[#EAE6DF]" data-testid="edit-product-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-sans text-[#2F3E3B]">Varegruppe</Label>
              <Select value={editProductGroup} onValueChange={setEditProductGroup}>
                <SelectTrigger className="border-[#EAE6DF] font-sans" data-testid="edit-product-group">
                  <SelectValue placeholder="Vælg varegruppe" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#EAE6DF]">
                  {groups.map(g => <SelectItem key={g.id} value={g.id} className="font-sans">{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-[#EAE6DF]">
            <Button variant="outline" onClick={handleDeleteProduct} disabled={productLoading}
              className="border-[#C85547] text-[#C85547] hover:bg-[#C85547] hover:text-white font-sans" data-testid="delete-product-button">
              Slet vare</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditingProduct(null)} className="font-sans">Annuller</Button>
              <Button onClick={handleSaveProduct} disabled={productLoading || !editProductName.trim()}
                className="bg-[#7F9C82] hover:bg-[#658368] text-white font-sans" data-testid="save-product-button">
                {productLoading ? 'Gemmer...' : 'Gem'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* User edit dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent className="bg-white border-[#EAE6DF]">
          <DialogHeader>
            <DialogTitle className="text-[#2F3E3B]">Rediger bruger</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-[#5C706A] font-sans">{editingUser?.email}</p>
            <div className="space-y-1.5">
              <Label className="font-sans text-[#2F3E3B]">Navn</Label>
              <Input value={editUserName} onChange={e => setEditUserName(e.target.value)}
                placeholder="Brugernavn" className="border-[#EAE6DF]" data-testid="edit-user-name"
                onKeyDown={e => e.key === 'Enter' && handleSaveUserName()} />
            </div>
            <div className="space-y-1.5">
              <Label className="font-sans text-[#2F3E3B]">Nyt password (lad være tomt for at beholde nuværende)</Label>
              <Input type="password" value={editUserPassword} onChange={e => setEditUserPassword(e.target.value)}
                placeholder="Nyt password..." className="border-[#EAE6DF]" data-testid="edit-user-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingUser(null)} className="font-sans">Annuller</Button>
            <Button onClick={handleSaveUserName} disabled={!editUserName.trim()}
              className="bg-[#7F9C82] hover:bg-[#658368] text-white font-sans" data-testid="save-user-button">Gem</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
